// ── BOOKKEEPING ASK GILDA ─────────────────────────────────────────────────────
// Bookkeeper-focused Ask Gilda. Lives under Procurement Management → Ask Gilda.
// Same UI shape as the general AskClaude, but the data context includes every
// finance-side ledger (vendor_payments, retainer_ledger, client_credit_ledger,
// vendor_credit_ledger, owner_draws, billing_summaries) and the system prompt
// is focused on accounting / cash flow / reconciliation questions.
//
// Visibility: gated to owner + bookkeeper at the tab level.

import { callClaude } from '../lib/claude';
import React, { useState, useRef, useEffect } from 'react';
import { useAppContext } from './AppContext';

const SUGGESTIONS = [
  "How much have we paid out to vendors this month?",
  "What's our outstanding retainer balance across all clients?",
  "Which clients have credit on file from cancellations?",
  "What's the current cash position estimate?",
  "Show me all vendor payments to Holly Hunt this year",
  "What's my year-to-date owner's draw total?",
  "Which POs have deposit paid but final still outstanding?",
  "What write-offs were posted this quarter?",
];

export default function BookkeepingAsk() {
  const ctx = useAppContext();
  const items = ctx.items || [];
  const projects = ctx.projects || [];
  const proposals = ctx.proposals || [];
  const purchaseOrders = ctx.purchaseOrders || [];
  const vendors = ctx.vendors || [];
  const vendorPayments = ctx.vendorPayments || [];
  const invoices = ctx.invoices || [];
  const retainerLedger = ctx.retainerLedger || [];
  const clientPayments = ctx.clientPayments || [];
  const paymentAllocations = ctx.paymentAllocations || [];
  const clientCreditLedger = ctx.clientCreditLedger || [];
  const vendorCreditLedger = ctx.vendorCreditLedger || [];
  const ownerDraws = ctx.ownerDraws || [];
  const userEmail = (ctx.userEmail || '').toLowerCase();

  const [query, setQuery] = useState('');
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [history]);

  function buildContext() {
    // Compact summaries — enough detail for bookkeeping questions, small enough
    // to fit in a Claude turn.
    const projectSummary = projects.map(p => ({
      label: p.label,
      clientId: p.clientId,
      hasRetainer: !!p.hasRetainer,
    }));

    const proposalSummary = proposals.map(p => ({
      ref: p.proposal_ref,
      project: p.project,
      status: p.status,
      itemCount: (p.items || []).length,
      totalCost: p.total_cost,
      clientPrice: p.total_client_price,
      depositPct: p.deposit_percent,
      paymentReceived: p.payment_amount || 0,
    }));

    const poSummary = purchaseOrders.map(po => ({
      ref: po.po_ref,
      vendor: po.vendorName,
      project: po.project,
      status: po.status,
      total: po.total_net,
      depositPct: po.deposit_pct,
      itemCount: (po.items || []).length,
    }));

    const vendorPaymentSummary = vendorPayments.map(p => ({
      vendor: p.vendor_name,
      project: p.project_label,
      amount: p.amount,
      type: p.payment_type,
      date: p.payment_date,
      reference: (p.reference || '').slice(0, 60),
    }));

    const invoiceSummary = invoices.map(inv => {
      const paid = paymentAllocations
        .filter(a => a.documentType === 'invoice' && a.documentId === String(inv.id))
        .reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);
      return {
        ref: inv.invoiceRef || inv.invoice_ref,
        type: inv.invoiceType,
        project: inv.project,
        client: inv.clientName,
        status: inv.status,
        amount: inv.amountDue || inv.amount,
        paid: paid,
        created: inv.createdAt,
      };
    });

    const clientPaymentSummary = clientPayments.map(p => ({
      clientId: p.clientId,
      date: p.paymentDate,
      amount: p.amountTotal,
      method: p.method,
      bankReference: p.bankReference,
      reference: (p.reference || '').slice(0, 60),
    }));

    const retainerSummary = retainerLedger.map(e => ({
      project: e.projectLabel,
      date: e.entryDate,
      type: e.entryType,
      amount: e.amount,
      reference: e.reference,
    }));

    const clientCreditSummary = clientCreditLedger.map(e => ({
      clientId: e.clientId,
      date: e.entryDate,
      type: e.entryType,
      amount: e.amount,
      reference: e.reference,
    }));

    const vendorCreditSummary = vendorCreditLedger.map(e => ({
      vendor: e.vendorName,
      date: e.entryDate,
      type: e.entryType,
      amount: e.amount,
      reference: e.reference,
    }));

    const ownerDrawSummary = ownerDraws.map(d => ({
      date: d.drawDate,
      amount: d.amount,
      category: d.category,
      reference: d.reference,
    }));

    const itemFinancials = items
      .filter(it => !it.parentId && it.status !== 'Cancelled')
      .map(it => ({
        sku: it.itemNumber,
        item: it.item,
        project: it.projectLabel || it.project,
        vendor: it.vendor,
        status: it.status,
        netCost: parseFloat(it.netPrice || 0),
        clientPrice: parseFloat(it.clientPrice || it.total || 0),
        depositPct: it.depositPct,
        orderPlaced: it.orderPlacedDate,
      }));

    return {
      projects: projectSummary,
      proposals: proposalSummary,
      purchaseOrders: poSummary,
      vendorPayments: vendorPaymentSummary,
      invoices: invoiceSummary,
      clientPayments: clientPaymentSummary,
      retainerLedger: retainerSummary,
      clientCreditLedger: clientCreditSummary,
      vendorCreditLedger: vendorCreditSummary,
      ownerDraws: ownerDrawSummary,
      items: itemFinancials,
    };
  }

  async function ask(q) {
    if (!q.trim()) return;
    const question = q.trim();
    setQuery('');
    setLoading(true);
    const newHistory = [...history, { role: 'user', content: question }];
    setHistory(newHistory);

    const ctxData = buildContext();
    const ctxStr = JSON.stringify(ctxData);

    const sys = `You are the bookkeeping assistant for Cimon Starr LLC, a high-end interior design firm in San Francisco. You have full access to the firm's financial data and answer questions for the owner (Cimon) and any team member with admin access configured in Firm Settings → Studio Team.

Answer accounting and cash-flow questions precisely. When the user asks for totals, compute them from the data. When ambiguous (date range not specified, etc.), ask a brief clarifying question.

Numbers should be formatted with thousands separators and cents where relevant ($1,234.56). Dates are in YYYY-MM-DD strings — interpret them as the actual calendar date, not UTC.

Be concise. Lead with the number or answer; follow with the breakdown only if useful. For lists, use bullet points.

Current date: ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
Current year: ${new Date().getFullYear()}

Key concepts in this data:
- Procurement: items → proposals → POs → invoices (deposit + final)
- Vendor side: vendor_payments tracks payments made; vendor_credit_ledger tracks cancellations/refunds/writeoffs
- Client side: invoices for billable items; client_credit_ledger tracks cancelled-item credits; retainer_ledger per project
- Owner draws: owner_draws records personal draws, amount is positive (it's a draw out)
- Cancelled items have status='Cancelled' and are excluded from active billing

DATA:
${ctxStr}`;

    try {
      const res = await fetch('/api/claude', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ system: sys, user: question, max_tokens: 1200 }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        const errMsg = data.error?.message || data.error || 'Status ' + res.status;
        setHistory([...newHistory, { role: 'assistant', content: 'Error: ' + errMsg }]);
        setLoading(false);
        return;
      }
      const text = data.text || 'Sorry, I could not find an answer.';
      setHistory([...newHistory, { role: 'assistant', content: text }]);
    } catch (err) {
      setHistory([...newHistory, { role: 'assistant', content: 'Error: ' + (err.message || String(err)) }]);
    }
    setLoading(false);
  }

  function handleKey(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      ask(query);
    }
  }

  function clearHistory() {
    setHistory([]);
  }

  const inp = {
    flex: 1, padding: '14px 18px', border: '1px solid #ddd',
    borderRadius: 8, fontSize: 16, fontFamily: 'inherit',
    outline: 'none', background: '#fff', resize: 'vertical',
    lineHeight: 1.5, minHeight: 110,
  };

  return (
    <div style={{ maxWidth: 1100, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 200px)', minHeight: 500, fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 18, fontWeight: 600, color: '#111', marginBottom: 4 }}>Ask Gilda — Bookkeeping</div>
        <div style={{ fontSize: 12, color: '#666' }}>
          Ask anything about cash position, vendor payments, retainer balances, credits, or draws. Full finance access — owner + bookkeeper only.
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'stretch' }}>
          <textarea
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKey}
            placeholder="e.g. Total paid to Holly Hunt this year? Show me POs with deposit paid but no final yet."
            rows={4}
            style={inp}
            autoFocus
          />
          <button onClick={() => ask(query)} disabled={!query.trim() || loading}
            style={{
              padding: '0 24px',
              background: query.trim() && !loading ? '#111' : '#e0e0e0',
              color: query.trim() && !loading ? '#fff' : '#aaa',
              border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600,
              cursor: query.trim() && !loading ? 'pointer' : 'not-allowed',
              whiteSpace: 'nowrap', minWidth: 110,
            }}>
            Ask →
          </button>
        </div>
        <div style={{ fontSize: 10, color: '#aaa', marginTop: 6, display: 'flex', justifyContent: 'space-between' }}>
          <span>Press Enter to send · Shift+Enter for new line</span>
          {history.length > 0 && (
            <button onClick={clearHistory} style={{ background: 'none', border: 'none', fontSize: 11, color: '#aaa', cursor: 'pointer', padding: 0 }}>
              Clear conversation
            </button>
          )}
        </div>
      </div>

      {history.length === 0 && (
        <div style={{ marginBottom: 14, paddingBottom: 14, borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#aaa', letterSpacing: 0.5, marginBottom: 8 }}>TRY ASKING</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {SUGGESTIONS.map((s, i) => (
              <button key={i} onClick={() => ask(s)} style={{
                padding: '6px 12px', background: '#f5f5f5', border: '1px solid #e0e0e0',
                borderRadius: 20, fontSize: 12, cursor: 'pointer', color: '#444',
              }}>{s}</button>
            ))}
          </div>
        </div>
      )}

      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12, paddingRight: 4 }}>
        {history.map((msg, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
            <div style={{
              maxWidth: '85%',
              padding: '14px 18px',
              borderRadius: msg.role === 'user' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
              background: msg.role === 'user' ? '#1a1a1a' : '#f5f5f3',
              color: msg.role === 'user' ? '#fff' : '#1a1a1a',
              fontSize: 14, lineHeight: 1.65, whiteSpace: 'pre-wrap',
            }}>
              {msg.content}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <div style={{
              padding: '12px 18px', borderRadius: '12px 12px 12px 2px',
              background: '#f5f5f3', fontSize: 14, color: '#888',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span style={{ display: 'inline-flex', gap: 3 }}>
                {[0,1,2].map(i => (
                  <span key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: '#aaa', animation: `bounce 1.2s ease-in-out ${i * 0.2}s infinite` }} />
                ))}
              </span>
              Working through the numbers…
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <style>{`@keyframes bounce { 0%, 60%, 100% { transform: translateY(0); } 30% { transform: translateY(-4px); } }`}</style>
    </div>
  );
}
